import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getSubscriptions } from '@/services'
import { AppButton, EmptyState, FilterChip, StatusBadge, PlanBadge, Skeleton, TableSkeleton } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { formatDate, formatCurrency } from '@/lib/utils'

const FILTERS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'Todas' },
  { id: 'active', label: 'Activas' },
  { id: 'trialing', label: 'Trial' },
  { id: 'past_due', label: 'Past due' },
  { id: 'cancelled', label: 'Canceladas' },
]

export function SubscriptionsPage() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')

  const { data: result, isLoading } = useQuery({
    queryKey: ['subscriptions', status],
    queryFn: () => getSubscriptions({ status: status === 'all' ? undefined : status, pageSize: 200 }),
  })
  const all = result?.data || []
  const subs = search
    ? all.filter((s) => [s.account_name, s.plan_name].some((v) => (v || '').toLowerCase().includes(search.toLowerCase())))
    : all
  const counts = all.reduce<Record<string, number>>((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc }, {})

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Billing</div>
          <h1>Subscriptions</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={270} height={14} /> : `${result?.count || all.length} subscriptions · ${counts.past_due || 0} requieren atención`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="download">Exportar</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar">
          <div className="table-search">
            <Icon name="search" size={14} />
            <input placeholder="Buscar por account o plan…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {FILTERS.map((f) => <FilterChip key={f.id} active={status === f.id} onClick={() => setStatus(f.id)} label={f.label} count={isLoading ? undefined : f.id === 'all' ? all.length : counts[f.id] || 0} />)}
        </div>
        {isLoading ? (
          <TableSkeleton columns={6} rows={8} />
        ) : subs.length === 0 ? (
          <EmptyState icon="refresh" title="Sin subscriptions" desc="No hay subscriptions con ese filtro." />
        ) : (
          <table className="table">
            <thead>
              <tr><th>Account</th><th>Plan</th><th>Status</th><th>Ciclo</th><th style={{ textAlign: 'right' }}>Precio</th><th>Próximo período</th></tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="cell-primary">{s.account_name || '—'}</div>
                    <div className="cell-secondary">{s.account_email || ''}</div>
                  </td>
                  <td><PlanBadge plan={s.plan_name} /></td>
                  <td><StatusBadge status={s.status} size="sm" /></td>
                  <td><span className="badge badge-outline badge-sm">{s.billing_cycle === 'monthly' ? 'Mensual' : 'Anual'}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="tabular cell-primary">{formatCurrency(s.plan_price || 0, 'ARS')}</span></td>
                  <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{s.current_period_end ? formatDate(s.current_period_end) : '—'}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
